if (!customElements.get('pg-hero-orbit')) {
  class PgHeroOrbit extends HTMLElement {
    constructor() {
      super();

      this.activeIndex = 0;
      this.slides = [];
      this.details = [];
      this.slideButtons = [];
      this.previousButton = null;
      this.nextButton = null;
      this.visibleStatus = null;
      this.liveStatus = null;
      this.wheel = null;
      this.motionFrame = null;
      this.motionEnabled = false;
      this.pendingMotion = null;
      this.reducedMotionQuery = null;
      this.finePointerQuery = null;

      this.onPrevious = () => this.show(this.activeIndex - 1, true);
      this.onNext = () => this.show(this.activeIndex + 1, true);
      this.onButtonClick = (event) => this.show(Number(event.currentTarget.dataset.pgIndex), true);
      this.onButtonKeydown = (event) => this.handleButtonKeydown(event);
      this.onBlockSelect = (event) => this.handleBlockSelect(event);
      this.onPointerMove = (event) => this.handlePointerMove(event);
      this.onPointerLeave = () => this.queueAmbientMotion(50, 42, 0, 0);
      this.onMotionPreferenceChange = () => this.configureAmbientMotion();
    }

    connectedCallback() {
      queueMicrotask(() => this.initialize());
    }

    disconnectedCallback() {
      if (this.dataset.pgReady !== 'true') return;

      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);

      this.slideButtons.forEach((button) => {
        button.removeEventListener('click', this.onButtonClick);
        button.removeEventListener('keydown', this.onButtonKeydown);
      });

      this.disableAmbientMotion();
      this.reducedMotionQuery?.removeEventListener?.('change', this.onMotionPreferenceChange);
      this.finePointerQuery?.removeEventListener?.('change', this.onMotionPreferenceChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      delete this.dataset.pgReady;
    }

    initialize() {
      if (!this.isConnected || this.dataset.pgReady === 'true') return;

      this.slides = Array.from(this.querySelectorAll('[data-pg-slide]'));
      this.details = Array.from(this.querySelectorAll('[data-pg-detail]'));
      this.slideButtons = Array.from(this.querySelectorAll('[data-pg-slide-button]'));
      this.previousButton = this.querySelector('[data-pg-previous]');
      this.nextButton = this.querySelector('[data-pg-next]');
      this.visibleStatus = this.querySelector('[data-pg-visible-status]');
      this.liveStatus = this.querySelector('[data-pg-status]');
      this.wheel = this.querySelector('.pg-hero-orbit__wheel');

      this.dataset.pgReady = 'true';
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.reducedMotionQuery.addEventListener?.('change', this.onMotionPreferenceChange);
      this.finePointerQuery.addEventListener?.('change', this.onMotionPreferenceChange);
      this.configureAmbientMotion();

      if (this.slides.length === 0) return;

      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);

      this.slideButtons.forEach((button) => {
        button.addEventListener('click', this.onButtonClick);
        button.addEventListener('keydown', this.onButtonKeydown);
      });

      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.show(0, false);
    }

    configureAmbientMotion() {
      const canAnimate = Boolean(
        this.wheel &&
        this.reducedMotionQuery &&
        !this.reducedMotionQuery.matches &&
        this.finePointerQuery?.matches
      );

      if (canAnimate === this.motionEnabled) return;

      if (canAnimate) {
        this.motionEnabled = true;
        this.dataset.pgAmbientMotion = 'true';
        this.wheel.addEventListener('pointermove', this.onPointerMove, { passive: true });
        this.wheel.addEventListener('pointerleave', this.onPointerLeave);
        return;
      }

      this.disableAmbientMotion();
    }

    disableAmbientMotion() {
      this.wheel?.removeEventListener('pointermove', this.onPointerMove);
      this.wheel?.removeEventListener('pointerleave', this.onPointerLeave);
      this.motionEnabled = false;
      delete this.dataset.pgAmbientMotion;
      this.pendingMotion = null;

      if (this.motionFrame !== null) {
        cancelAnimationFrame(this.motionFrame);
        this.motionFrame = null;
      }

      this.style.removeProperty('--pg-pointer-x');
      this.style.removeProperty('--pg-pointer-y');
      this.style.removeProperty('--pg-orbit-shift-x');
      this.style.removeProperty('--pg-orbit-shift-y');
    }

    handlePointerMove(event) {
      if (!this.motionEnabled || !this.wheel) return;

      const bounds = this.wheel.getBoundingClientRect();
      if (bounds.width === 0 || bounds.height === 0) return;

      const normalizedX = Math.min(1, Math.max(-1, ((event.clientX - bounds.left) / bounds.width - 0.5) * 2));
      const normalizedY = Math.min(1, Math.max(-1, ((event.clientY - bounds.top) / bounds.height - 0.5) * 2));

      this.queueAmbientMotion(
        50 + normalizedX * 20,
        42 + normalizedY * 18,
        normalizedX * 0.42,
        normalizedY * 0.32
      );
    }

    queueAmbientMotion(pointerX, pointerY, shiftX, shiftY) {
      if (!this.motionEnabled) return;

      this.pendingMotion = { pointerX, pointerY, shiftX, shiftY };
      if (this.motionFrame !== null) return;

      this.motionFrame = requestAnimationFrame(() => {
        this.motionFrame = null;
        if (!this.pendingMotion || !this.motionEnabled) return;

        const motion = this.pendingMotion;
        this.pendingMotion = null;
        this.style.setProperty('--pg-pointer-x', `${motion.pointerX.toFixed(2)}%`);
        this.style.setProperty('--pg-pointer-y', `${motion.pointerY.toFixed(2)}%`);
        this.style.setProperty('--pg-orbit-shift-x', `${motion.shiftX.toFixed(3)}rem`);
        this.style.setProperty('--pg-orbit-shift-y', `${motion.shiftY.toFixed(3)}rem`);
      });
    }

    show(requestedIndex, announce) {
      const total = this.slides.length;
      if (total === 0 || !Number.isFinite(requestedIndex)) return;

      const index = ((requestedIndex % total) + total) % total;
      this.activeIndex = index;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.hidden = !isActive;
        slide.classList.toggle('is-active', isActive);
      });

      this.details.forEach((detail, detailIndex) => {
        const isActive = detailIndex === index;
        detail.hidden = !isActive;
        detail.classList.toggle('is-active', isActive);
      });

      this.slideButtons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === index;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
        button.tabIndex = isActive ? 0 : -1;
      });

      if (this.visibleStatus) {
        this.visibleStatus.textContent = `${String(index + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
      }

      if (announce && this.liveStatus) {
        const title = this.slides[index].dataset.pgProductTitle || '';
        this.liveStatus.textContent = `Item ${index + 1} of ${total}: ${title}`;
      }
    }

    handleButtonKeydown(event) {
      let targetIndex;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          targetIndex = this.activeIndex - 1;
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          targetIndex = this.activeIndex + 1;
          break;
        case 'Home':
          targetIndex = 0;
          break;
        case 'End':
          targetIndex = this.slides.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.show(targetIndex, true);
      this.slideButtons[this.activeIndex]?.focus();
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId;
      if (!blockId) return;

      const selectedIndex = this.slides.findIndex((slide) => slide.dataset.pgBlockId === blockId);
      if (selectedIndex >= 0) this.show(selectedIndex, false);
    }
  }

  customElements.define('pg-hero-orbit', PgHeroOrbit);
}
